import { format } from 'date-fns';

import xws from 'api/xws/';

// Action Types
import * as actionTypes from './actionTypes';

// Actions
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import { callSaveStats } from 'store/reducers/dealerData/actions';
import { getAutoLogin } from 'store/reducers/login/actions';
import {
  getAppointmentDetails,
  setContactInfo,
  getMetaVehicleId,
  setCustomerVehicle,
  addService,
  setParticipantComments,
  setApptServicePoint,
  selectMake,
  selectYear,
  selectModel,
  setMileage,
  setTransmission,
  setRegDate,
  setLicenseNumber,
  setMetaVehicleId,
  setTransportationOption,
  selectQuickApptOptions,
  setStartTime,
  setTeamIds
} from 'store/reducers/appointment/actions';
import { getMarketing } from 'store/reducers/marketing/actions';
import {
  getPromoList,
  setActivePromotion,
  getAnonymousPromotion
} from 'store/reducers/promotions/actions';
import {
  getDecodeVIN,
  setDecodedVINtoAppt
} from 'store/reducers/vehicle/actions';
import { getYears } from 'store/reducers/years/actions';
import { getModels } from 'store/reducers/models/actions';
import { getTrims } from 'store/reducers/trims/actions';
import { setAppFlow } from 'store/reducers/userInteractions/actions';
import { goTo } from 'store/reducers/navigation/actions';
import {
  gtmUserEvent,
  gtmStartGenericEvent,
  gtmSetDimensionConfirmationKey
} from 'store/reducers/gtm/actions';
import { addQueryParams } from 'store/reducers/queryParams/actions';
import { getApptAdvisorsAndTranspOptions } from 'store/reducers/apptOptions/actions';
import { getFirstAvailability } from 'store/reducers/availableTimes/actions';
import {
  setQuoteConfirmationId,
  setQuotingAppointment,
  setIsQuoteAppt,
  setShowQuoteApptError
} from 'store/reducers/quoteAppt/actions';

// Selectors
import {
  getQueryParam,
  getUnObfuscatedObject,
  getWebkey
} from 'store/reducers/queryParams/selectors';
import { getDeferredServices } from 'store/reducers/deferred/selectors';
import { getDeclinedServices } from 'store/reducers/declined/selectors';
import { getAutoRecalls } from 'store/reducers/recall/selectors';
import { validateTrim, getFirstTrim } from 'store/reducers/trims/selectors';
import {
  getDeeplinkKind,
  getVerifiedVehicle,
  getIncompleteVehicle,
  getApptRevisit,
  getHasServices,
  getHasTransportationOptions,
  getIsDeeplinking,
  getIsDeeplink
} from 'store/reducers/deeplink/selectors';
import {
  getPersonId as getPersonIdFromLogin,
  getUserVehicleByVIN
} from 'store/reducers/login/selectors';
import {
  makeSelectedSelector as getMake,
  getYear,
  getContactInfo,
  isPastAppointment,
  getConfirmationKey,
  getTransportation
} from 'store/reducers/appointment/selectors';
import {
  makes as getSupportedMakes,
  findMake
} from 'store/reducers/dealerSettings/selectors';
import { getTransportOptions } from 'store/reducers/apptOptions/selectors';
import {
  getQuickApptEnabled,
  getLocalizedQuickApptService
} from 'store/reducers/userInteractions/selectors';
import {
  getFirstAvailableTimeSlot,
  getFirstAvailableTimeSlotTeamsId
} from 'store/reducers/firstAvailableTimeSlot/selectors';

// Utils
import { isTrue } from 'utils/varValidation';
import { calcPackageTotalPrice } from 'utils/prices';
import { isValidRegistrationDate } from 'utils/date';

// Constants
import {
  DEEPLINK_PAGES,
  DECLINED_SERVICES_ROUTE,
  RECALL_SERVICES_ROUTE,
  USER_VEHICLES_ROUTE
} from 'constants/routes';
import { APP_FLOW } from 'constants/appFlow';
import { I_DONT_KNOW, VEHICLE_VALUES } from 'constants/vehicle';
import { TRANSPORTATION } from 'constants/transportation';
import { getService } from 'constants/ajax';
import { NEW_APPT } from 'constants/deeplink';

const { VALET, VALETLOANER } = TRANSPORTATION;

export const deeplinkInit = () => async (dispatch, getState) => {
  if (getIsDeeplink(getState())) {
    await dispatch(readUserData());
    await dispatch(readVehicleData());
    await dispatch(readApptData());
    await dispatch(readQuickApptData());
    await dispatch(readQuotingData());
    await dispatch(defineNavigation());
    dispatch(setIsDeeplinking(false));
  }
};

const isAutoLogin = () => (dispatch, getState) => {
  const { username, password } = getUnObfuscatedObject(getState());
  return Boolean(username) && Boolean(password);
};

export const getApptFindParams = () => (dispatch, getState) => {
  let confirmationKey = null;
  let email = null;
  const { confKey: confKeyW, userEmail: userEmailW } = getUnObfuscatedObject(
    getState()
  );
  if (confKeyW && userEmailW) {
    confirmationKey = confKeyW;
    email = userEmailW;
  } else if (confKeyW) {
    confirmationKey = confKeyW;
  } else {
    confirmationKey = getQueryParam('confkey')(getState()) || null;
    email = getQueryParam('cem')(getState()) || null;
  }
  return {
    confirmationKey,
    email
  };
};

const isApptFind = () => (dispatch, getState) => {
  const { confirmationKey } = dispatch(getApptFindParams());
  const scope = getQueryParam('scope')(getState());
  // @TODO: review this flag
  return Boolean(confirmationKey) || (confirmationKey && scope === NEW_APPT);
};

export const couldBeApptDeeplink = () => (dispatch, getState) => {
  return dispatch(isApptFind()) || dispatch(isAutoLogin());
};

export const isDeeplink = () => (dispatch, getState) => {
  let isDeeplinking = getIsDeeplinking(getState());
  if (isDeeplinking) {
    dispatch(setIsDeeplink(isDeeplinking));
    return isDeeplinking;
  }
  const isMarketing = Boolean(getQueryParam('token')(getState()));
  const isVehicle = dispatch(hasVehicleInfoInURL());
  const isUser = dispatch(hasUserInfoInURL());
  const isQuickAppt = getQueryParam('dest')(getState()) === APP_FLOW.IN_HURRY;
  const isQuote = getQueryParam('quoteConfirmationId')(getState());
  isDeeplinking =
    dispatch(isAutoLogin()) ||
    isMarketing ||
    dispatch(isApptFind()) ||
    isVehicle ||
    isUser ||
    isQuickAppt ||
    isQuote;
  dispatch(setIsDeeplink(isDeeplinking));
  dispatch(setIsDeeplinking(isDeeplinking));
  return isDeeplinking;
};

const hasVehicleInfoInURL = () => (dispatch, getState) => {
  return (
    Boolean(getQueryParam('make')(getState())) ||
    Boolean(getQueryParam('vin')(getState())) ||
    Boolean(getQueryParam('chromeStyleId')(getState()))
  );
};

const hasUserInfoInURL = () => (dispatch, getState) => {
  return (
    Boolean(getQueryParam('cfn')(getState())) ||
    Boolean(getQueryParam('cln')(getState())) ||
    Boolean(getQueryParam('cpn')(getState())) ||
    Boolean(getQueryParam('cem')(getState()))
  );
};

/**
 * for autoLogin, Appt Find, Marketing
 */
const readUserData = () => async (dispatch, getState) => {
  if (dispatch(isAutoLogin())) {
    await dispatch(readAutoLogin());
  } else {
    await dispatch(readToken());
    await dispatch(readApptFind());
    await dispatch(readUserFromQueryParam());
  }
};

const readAutoLogin = () => async (dispatch, getState) => {
  const autoLoginData = await dispatch(autoLogin());
  if (autoLoginData) {
    const { reservationId } = getUnObfuscatedObject(getState());
    if (isTrue(getQueryParam('is_sso')(getState()))) {
      dispatch(setDeepLinkKind('sso'));
    } else {
      dispatch(setDeepLinkKind('autoLogin'));
    }
    if (reservationId) {
      // search and select appt
      try {
        const personId = getPersonIdFromLogin(getState());
        const apptData = await dispatch(
          getAppointmentDetails({
            personId,
            reservationId
          })
        );
        if (apptData) {
          dispatch(setAppRevisit(true));
          dispatch(setAppFlow(APP_FLOW.LOG_ME_IN));
        } else {
          dispatch(setAppRevisit(false));
        }
        dispatch(pushGTMStartEvent());
      } catch (e) {
        dispatch(pushGTMStartEvent());
        dispatch(setAppRevisit(false));
      }
    } else {
      const { firstName, lastName, contactInfo } = autoLoginData;
      const {
        emailAddress: { email }
      } = contactInfo;
      const { digits: phone } =
        contactInfo.phoneNumbers.find(pn => pn.digits) || {};
      dispatch(
        setContactInfo({
          firstName,
          lastName,
          phone,
          email
        })
      );
      // search and select vehicle
      const deeplinkingVIN = getQueryParam('vin')(getState());
      const userVehicle = getUserVehicleByVIN(getState())(deeplinkingVIN);
      if (userVehicle) {
        const {
          transmissionType,
          driveType,
          trim,
          engineSize,
          engineType,
          make,
          year,
          model,
          vin,
          metaVehicleId,
          mileage,
          regDate,
          licenseNumber,
          vehicleId
        } = userVehicle;
        await dispatch(
          setCustomerVehicle({
            transmissionType,
            driveType,
            trim,
            engineSize,
            engineType,
            make,
            year,
            model,
            vin,
            metaVehicleId,
            mileage,
            regDate,
            licenseNumber,
            vehicleId
          })
        );
        dispatch(setVerifiedVehicle(true));
      }
      dispatch(setAppFlow(APP_FLOW.LOG_ME_IN));
      dispatch(pushGTMStartEvent());
    }
  }
};

const autoLogin = () => async (dispatch, getState) => {
  try {
    const data = await dispatch(getAutoLogin());
    if (data) {
      dispatch(setDeepLinkKind('autoLogin'));
    } else {
      dispatch(pushGTMStartEvent());
    }
    return data;
  } catch (e) {
    dispatch(pushGTMStartEvent());
    return false;
  }
};

const readToken = () => async (dispatch, getState) => {
  if (getDeeplinkKind(getState())) {
    return; // deeplinking is defined no more validation should be done
  }
  if (getQueryParam('token')(getState())) {
    try {
      const marketingData = await dispatch(getMarketing());
      if (marketingData) {
        if (getQueryParam('variant')(getState()) && marketingData.variant) {
          marketingData.variant = getQueryParam('variant')(getState());
        }
        /* eslint-disable camelcase */
        const {
          cem,
          cfn,
          cln,
          cpn,
          vin,
          note,
          provider,
          keyword,
          campaign_id
        } = marketingData;
        dispatch(setDeepLinkKind('marketing'));
        dispatch(callSaveStats(provider, keyword, campaign_id));
        /* eslint-enable camelcase */
        const validVin = await dispatch(readVIN(vin, marketingData));
        if (validVin) {
          dispatch(
            setContactInfo({
              firstName: cfn,
              lastName: cln,
              phone: cpn || '',
              email: cem
            })
          );
          if (marketingData.promoCode) {
            const promoList = await dispatch(
              getPromoList(marketingData.vin, marketingData.promoCode)
            );
            dispatch(setActivePromotion(promoList[0]));
          }
          dispatch(setParticipantComments(note));
        } else if (marketingData.promoCode) {
          dispatch(getAnonymousPromotion(marketingData.promoCode));
        }
      }
    } catch (e) {
      /* eslint-disable no-console */
      console.log('error: ', e);
      /* eslint-enable no-console */
    }
  }
};

const readApptFind = () => async (dispatch, getState) => {
  const { confirmationKey, email } = dispatch(getApptFindParams());
  const scope = getQueryParam('scope')(getState());
  if (getDeeplinkKind(getState())) {
    return; // deeplinking is defined no more validation should be done
  }
  // Deeplink for recreate cancelled or past appt
  if (scope === NEW_APPT && confirmationKey) {
    try {
      const data = await dispatch(
        getAppointmentDetails({
          confirmationKey,
          scope
        })
      );
      if (data) {
        const {
          personInfo: { firstName, lastName, phone, mobilePhone, email, extId }
        } = data;
        dispatch(
          setContactInfo({
            firstName,
            lastName,
            phone,
            mobilePhone,
            email,
            obfuscatedBy: 'licenseNumber',
            extId
          })
        );
        dispatch(setDeepLinkKind('SCI'));
        dispatch(setVerifiedVehicle(true));
        dispatch(setHasServices(true));
        dispatch(setHasTransportationOptions(true));
        dispatch(setAppRevisit(false));
        dispatch(setAppFlow(APP_FLOW.FIND_ME));
        dispatch(
          gtmUserEvent(
            'Self Check-in Link',
            'Clicked',
            'Clicked on Self Check-in link, landed on ?',
            'SCI > ?'
          )
        );
      }
    } catch (e) {
      /* eslint-disable no-console */
      console.log('error: ', e);
      /* eslint-enable no-console */
    }
  }
  if (confirmationKey && email) {
    try {
      const data = await dispatch(
        getAppointmentDetails({
          confirmationKey,
          email
        })
      );
      if (data) {
        const {
          personInfo: { firstName, lastName, phone, mobilePhone, email }
        } = data;
        dispatch(
          setContactInfo({
            firstName,
            lastName,
            phone,
            mobilePhone,
            email
          })
        );
        dispatch(setDeepLinkKind('readApptFind'));
        dispatch(setVerifiedVehicle(true));
        dispatch(setAppRevisit(true));
        dispatch(setAppFlow(APP_FLOW.FIND_ME));
        dispatch(
          gtmUserEvent(
            'Email link',
            'Clicked',
            'Clicked on email link, landed on review page',
            'Email > Review'
          )
        );
      } else {
        dispatch(setAppRevisit(false));
      }
      dispatch(pushGTMStartEvent());
    } catch (e) {
      dispatch(pushGTMStartEvent());
      dispatch(setAppRevisit(false));
    }
  }
  if (confirmationKey && !email && !scope) {
    try {
      const data = await dispatch(
        getAppointmentDetails({
          confirmationKey
        })
      );
      if (data) {
        const {
          personInfo: { firstName, lastName, phone, mobilePhone, email }
        } = data;
        dispatch(
          setContactInfo({
            firstName,
            lastName,
            phone,
            mobilePhone,
            email,
            obfuscatedBy: 'deeplink'
          })
        );
        dispatch(setDeepLinkKind('readApptFind'));
        dispatch(setVerifiedVehicle(true));
        dispatch(setAppRevisit(true));
        dispatch(setAppFlow(APP_FLOW.FIND_ME));
      } else {
        dispatch(setAppRevisit(false));
      }
      dispatch(pushGTMStartEvent());
    } catch (e) {
      /* eslint-disable no-console */
      console.log('error: ', e);
      /* eslint-enable no-console */
    }
  }
};

const readUserFromQueryParam = () => async (dispatch, getState) => {
  if (getDeeplinkKind(getState())) {
    return; // deeplinking is defined no more validation should be done
  }
  const stateContactInfo = getContactInfo(getState());
  const contactInfo = {};
  const setQueryData = (field, qField) => {
    if (stateContactInfo[field]) {
      contactInfo[field] = stateContactInfo[field];
    } else {
      const data = getQueryParam(qField)(getState()) || '';
      contactInfo[field] = data;
    }
  };
  setQueryData('firstName', 'cfn');
  setQueryData('lastName', 'cln');
  setQueryData('phone', 'cpn');
  setQueryData('email', 'cem');
  try {
    dispatch(setContactInfo(contactInfo));
  } catch (e) {
    /* eslint-disable no-console */
    console.log('error: ', e);
    /* eslint-enable no-console */
  }
};

const isVehicleByChromeStyleId = chromeId => async (dispatch, getState) => {
  if (chromeId) {
    const webkey = getWebkey(getState());
    const chromeStyleIdService = getService('vehicleByChromeStyleId');
    const licenseNumber = getQueryParam('regnum')(getState());
    const vehRegDate = getQueryParam('registration_date')(getState());
    const regDate = dispatch(getVehRegDate(vehRegDate));
    dispatch(setApptMileage());
    chromeStyleIdService.setPathVars({ webkey, chromeId });
    chromeStyleIdService.setCallBacks(dispatch(ajaxCallbacks()));
    const request = xws(chromeStyleIdService).get(
      chromeStyleIdService.getPath()
    );
    try {
      const {
        data: { data }
      } = await request;
      if (data.length > 0) {
        const vehicleData = data[0];
        const year = data[0].year.toString();
        await dispatch(setMetaVehicleId(data.id));
        await dispatch(
          setCustomerVehicle({
            ...vehicleData,
            year,
            regDate,
            licenseNumber
          })
        );
        return data;
      }
      return false;
    } catch (e) {
      /* eslint-disable no-console */
      console.log('error:', e);
      /* eslint-enable no-console */
      return false;
    }
  }
  return false;
};

const isQuickApptEnabled = () => (dispatch, getState) => {
  const isDestInHurry = getQueryParam('dest')(getState()) === APP_FLOW.IN_HURRY;
  const isQuickApptEnabled = getQuickApptEnabled(getState());
  return isDestInHurry && isQuickApptEnabled;
};

const readVehicleData = () => async (dispatch, getState) => {
  if (getVerifiedVehicle(getState())) {
    return; // vehicle data was verified
  } else {
    const licenseNumber = getQueryParam('regnum')(getState());
    const vin = getQueryParam('vin')(getState());
    if (vin) {
      const decodedVIN = await dispatch(readVIN(vin));
      if (!decodedVIN) {
        await dispatch(newVehicle());
      }
    } else {
      await dispatch(newVehicle());
    }
    if (licenseNumber) {
      dispatch(setLicenseNumber(licenseNumber));
    }
  }
};

const readVIN = (vin, marketingData) => async (dispatch, getState) => {
  try {
    const decodedVIN = await dispatch(getDecodeVIN(vin));
    if (decodedVIN) {
      marketingData && dispatch(addQueryParams(marketingData));
      await dispatch(setDecodedVINtoAppt({ vin, ...decodedVIN }));
      dispatch(setVerifiedVehicle(true));
      dispatch(setAppFlow(APP_FLOW.IM_NEW_HERE));
    }
    return decodedVIN;
  } catch (e) {
    /* eslint-disable no-console */
    console.log('error: ', e);
    /* eslint-enable no-console */
  }
};

const newVehicle = () => async (dispatch, getState) => {
  if (getVerifiedVehicle(getState())) {
    return; // vehicle data was verified
  }
  const chromeId = getQueryParam('chromeStyleId')(getState());
  const vehicleByChromeStyleId = await dispatch(
    isVehicleByChromeStyleId(chromeId)
  );
  if (vehicleByChromeStyleId) {
    dispatch(setVerifiedVehicle(true));
    dispatch(setAppFlow(APP_FLOW.IM_NEW_HERE));
  } else {
    const trim = getQueryParam('trim')(getState()) || I_DONT_KNOW;
    const make = getQueryParam('make')(getState());
    const year = getQueryParam('year')(getState());
    const model = getQueryParam('model')(getState());
    const engineType = getQueryParam('engine_type')(getState());
    const engineSize = getQueryParam('engine_size')(getState());
    const driveType = getQueryParam('drive_type')(getState());
    const transmissionType = getQueryParam('transmission_type')(getState());
    const vehRegDate = getQueryParam('registration_date')(getState());
    const licenseNumber = getQueryParam('regnum')(getState());
    const vto = getQueryParam('vto')(getState());
    const mileage = getQueryParam('mileage')(getState());
    let trimData = null;

    if (!make) {
      return;
    }

    const makeData = findMake(getState())(make);
    if (!makeData) {
      return;
    }

    const supportedMake = makeData.make;
    let supportedYear = null;
    let supportedModel = null;

    try {
      if (supportedMake) {
        const years = await dispatch(selectMake(supportedMake, true));
        supportedYear = years.find(y => y === year);
        if (supportedYear) {
          const models = await dispatch(selectYear(year, true));
          const supportedModelData = models.find(
            m => m.data.toLowerCase() === model.toLowerCase()
          );
          if (supportedModelData && supportedModelData.data) {
            supportedModel = supportedModelData.data;
          }
          if (supportedModel) {
            await dispatch(selectModel(supportedModel));
          }
        }
      }

      let vehicleData = { make: supportedMake, year, model };
      dispatch(validateMake(vehicleData));
      dispatch(setApptMileage());

      const regDate = dispatch(getVehRegDate(vehRegDate));

      if (I_DONT_KNOW !== trim) {
        trimData = {
          trim,
          engineType,
          engineSize,
          driveType,
          transmissionType
        };
        trimData = validateTrim(getState(), trimData);
        if (!trimData) {
          trimData = getFirstTrim(getState(), trim);
        }
      }
      if (trimData) {
        vehicleData = { ...vehicleData, ...trimData };
      } else {
        if (vto !== 'Y') {
          // Just for newVehicle flow, if trim does not have value, means that trim value is I_DONT_KNOW
          dispatch(addQueryParams({ trim: I_DONT_KNOW }));
        }
      }
      const decodedVehicle = await dispatch(getMetaVehicleId(vehicleData));
      if (decodedVehicle.id) {
        dispatch(setVerifiedVehicle(true));
        vehicleData = { ...vehicleData, model: decodedVehicle.model };
        await dispatch(
          setCustomerVehicle({
            ...vehicleData,
            mileage,
            regDate,
            licenseNumber
          })
        );
        if (trim.toLowerCase() === I_DONT_KNOW.toLowerCase()) {
          await dispatch(setTransmission(''));
        }
        dispatch(setAppFlow(APP_FLOW.IM_NEW_HERE));
      }
    } catch (e) {
      if (supportedMake && supportedYear) {
        dispatch(setIsIncompleteVehicle(true));
        dispatch(setAppFlow(APP_FLOW.IM_NEW_HERE));
      }
    }
  }
};

const validateMake = vehicleData => (dispatch, getState) => {
  const supportedMakes = getSupportedMakes(getState());
  if (supportedMakes.find(make => make.data === vehicleData.make)) {
    return;
  } else if (supportedMakes.find(make => make.data === VEHICLE_VALUES.OTHER)) {
    vehicleData.make = VEHICLE_VALUES.OTHER;
  } else {
    vehicleData.make = null;
  }
};

export const setSabService = serviceList => async (dispatch, getState) => {
  const sab = getQueryParam('sab')(getState());
  if (sab) {
    const sabArray = sab.split(':');
    let sabIndex;
    let serviceIndex;
    for (sabIndex = 0; sabIndex < sabArray.length; sabIndex++) {
      for (
        serviceIndex = 0;
        serviceIndex < serviceList.length;
        serviceIndex++
      ) {
        if (serviceList[serviceIndex].abbreviation === sabArray[sabIndex]) {
          await dispatch(setHasServices(true));
          dispatch(
            addService({
              ...serviceList[serviceIndex],
              notes: 'This service was selected automatically'
            })
          );
          break;
        }
      }
    }
  }
};

const mapDeeplinkServices = (servicesArray, serviceList) => async dispatch => {
  let servicesFound = false;
  for (let k = 0; k < servicesArray.length; k++) {
    for (let i = 0; i < serviceList.length; i++) {
      if (+serviceList[i].serviceCategoryId === +servicesArray[k]) {
        servicesFound = true;
        await dispatch(setHasServices(true));
        dispatch(
          addService({
            ...serviceList[i],
            notes: 'This service was selected automatically'
          })
        );
        break;
      }
    }
  }
  return servicesFound;
};

export const setServiceCategories = serviceList => async (
  dispatch,
  getState
) => {
  const services = getQueryParam('serviceCategory')(getState());
  const defaultCategory = getQueryParam('defaultCategory')(getState());
  if (services || defaultCategory) {
    const servicesArray = services ? services.split(':') : [];
    const defaultsArray = defaultCategory ? defaultCategory.split(':') : [];
    const servicesFound = await dispatch(
      mapDeeplinkServices(servicesArray, serviceList)
    );
    if (!servicesFound) {
      await dispatch(mapDeeplinkServices(defaultsArray, serviceList));
    }
  }
};

export const setMenuType = servicePoint => async (dispatch, getState) => {
  const menuType = getQueryParam('menu_type')(getState());

  if (menuType) {
    const optionalServices = getQueryParam('optional_services')(getState());
    const { menuTypes, ...servicePointProps } = servicePoint;
    const selectedPackage = servicePoint.menuTypes.find(
      pack => pack.name === menuType
    );
    const optionalServicesArray = optionalServices
      ? optionalServices.split('|')
      : [];
    const selectedServices = [];
    const allServices = [];
    // TODO: refactor race-condition generated by this forEach
    selectedPackage.services.forEach(service => {
      if (
        optionalServicesArray.find(
          optionalService => +optionalService === service.id
        ) ||
        !service.selectable
      ) {
        selectedServices.push(service);
        allServices.push({ ...service, isSelected: true });
      } else {
        allServices.push(service);
      }
    });
    const totalPrice = calcPackageTotalPrice(
      allServices,
      selectedPackage.totalPrice
    );
    const formattedPackage = {
      ...servicePointProps,
      menuType: {
        ...selectedPackage,
        services: selectedServices,
        price: totalPrice,
        totalPrice
      }
    };

    await dispatch(setApptServicePoint(formattedPackage));
    dispatch(setHasServices(true));
  }
};

const readApptData = () => async (dispatch, getState) => {
  if (getApptRevisit(getState())) {
    return; // service data was verified
  }
  const hasServices = getHasServices(getState());
  const transportationType = getQueryParam('transportationType')(getState());
  if (transportationType && hasServices) {
    await dispatch(getApptAdvisorsAndTranspOptions());
    const availableTransportationOptions = getTransportOptions(getState());
    const selectedTransportationOptions = availableTransportationOptions.filter(
      transOpt =>
        transOpt.data.toLowerCase() === transportationType.toLowerCase()
    );
    if (selectedTransportationOptions.length > 0) {
      dispatch(setTransportationOption(selectedTransportationOptions[0].data));
      dispatch(setHasTransportationOptions(true));
    }
  }
  dispatch(setApptNote());
  dispatch(setApptMileage());
};

const readQuickApptData = () => async (dispatch, getState) => {
  const quickApptEnabled = dispatch(isQuickApptEnabled());
  if (quickApptEnabled) {
    dispatch(setAppFlow(APP_FLOW.QUICK_APPT));
    if (!getVerifiedVehicle(getState())) {
      const { OTHER } = VEHICLE_VALUES;
      const vehicle = {
        make: OTHER,
        year: new Date().getFullYear() - 1,
        model: OTHER
      };
      await dispatch(setCustomerVehicle(vehicle));
    }
    if (!getTransportOptions(getState())) {
      await dispatch(getApptAdvisorsAndTranspOptions());
    }
    const tellusMoreText = getLocalizedQuickApptService(getState());
    await dispatch(setHasServices(true));
    await dispatch(selectQuickApptOptions(tellusMoreText));
    await dispatch(getFirstAvailability());
    const firstAvailableSlot = getFirstAvailableTimeSlot(getState());
    const firstAvailableSlotTeamsId = getFirstAvailableTimeSlotTeamsId(
      getState()
    );
    dispatch(setStartTime(firstAvailableSlot));
    dispatch(setTeamIds(firstAvailableSlotTeamsId));
    dispatch(setDeepLinkKind('quickAppt'));
  }
};

// eslint-disable-next-line no-unused-vars
const callVehicleServerServices = () => (dispatch, getState) => {
  dispatch(getYears(getMake(getState())));
  dispatch(getModels(getYear(getState())));
  dispatch(getTrims());
};

const getStep = enoughDataStep => async (dispatch, getState) => {
  const dest = getQueryParam('dest')(getState()) || '';
  const vto = getQueryParam('vto')(getState());
  const trim = getQueryParam('trim')(getState());
  const vin = getQueryParam('vin')(getState());
  const deeplinkKind = getDeeplinkKind(getState());
  const navigationArray = [
    'START',
    'VEHICLE',
    'SERVICE',
    'TRANS',
    'APPT',
    'REVIEW',
    'SH',
    'ST'
  ];
  let destPosition = navigationArray.indexOf(dest.toUpperCase());
  let enoughDataStepPosition = navigationArray.indexOf(enoughDataStep);
  if (enoughDataStepPosition === 6) {
    dispatch(goTo(USER_VEHICLES_ROUTE));
    return null;
  }
  if (deeplinkKind !== 'sso') {
    if (destPosition > 5) {
      destPosition = 2;
    }
  }
  // As in CP3, we can only support a trim with "I DON'T KNOW" if and only if VTO is 'Y',
  // otherwise, we can only go as maximum to the VEHICLES page.
  if (I_DONT_KNOW === trim && !(vto === 'Y') && deeplinkKind !== 'quickAppt') {
    const vehiclePageIdx = navigationArray.indexOf('VEHICLE');
    if (enoughDataStepPosition > vehiclePageIdx)
      enoughDataStepPosition = vehiclePageIdx;
  }

  const stepByDefault = navigationArray.indexOf('SERVICE');

  if (destPosition > -1) {
    if (destPosition >= 6 && !vin) {
      // special case SH ST
      dispatch(goTo(DEEPLINK_PAGES[destPosition]));
    } else if (destPosition >= 6) {
      destPosition = stepByDefault;
    }
    const destNavigation = Math.min(destPosition, enoughDataStepPosition);
    dispatch(goTo(DEEPLINK_PAGES[destNavigation]));
  } else {
    if (enoughDataStep !== 'REVIEW') {
      if (
        getDeclinedServices(getState()).concat(getDeferredServices(getState()))
          .length > 0
      ) {
        dispatch(goTo(DECLINED_SERVICES_ROUTE));
        return null;
      }
      if (getAutoRecalls(getState()).length > 0) {
        dispatch(goTo(RECALL_SERVICES_ROUTE));
        return null;
      }
      let computedStep = 0;
      if (deeplinkKind === 'SCI') {
        computedStep = enoughDataStepPosition;
      } else {
        computedStep = Math.min(enoughDataStepPosition, stepByDefault);
      }
      dispatch(goTo(DEEPLINK_PAGES[computedStep]));
      return computedStep;
    } else {
      dispatch(goTo(DEEPLINK_PAGES[enoughDataStepPosition]));
      return enoughDataStepPosition;
    }
  }
};

const getVehRegDate = vehRegDate => dispatch => {
  let regDate;
  if (isValidRegistrationDate(vehRegDate)) {
    regDate = format(vehRegDate, 'YYYY-MM-DD');
    dispatch(setRegDate(regDate));
  }
  return regDate;
};

const setApptNote = () => (dispatch, getState) => {
  const note = getQueryParam('note')(getState());
  if (note) {
    dispatch(setParticipantComments(note));
  }
};

const setApptMileage = () => (dispatch, getState) => {
  const mileage = getQueryParam('mileage')(getState());
  if (mileage) {
    dispatch(setMileage(mileage, false));
  }
};

const readQuotingData = () => async (dispatch, getState) => {
  const isQuoteAppt = getQueryParam('quoteConfirmationId')(getState());
  if (!isQuoteAppt) {
    return;
  }
  const webkey = getWebkey(getState());
  const quoteConfirmationId = getQueryParam('quoteConfirmationId')(getState());
  dispatch(setQuoteConfirmationId(quoteConfirmationId));
  const getQuotingDetailsService = getService('quotingDetails');
  getQuotingDetailsService.setPathVars({ webkey, quoteConfirmationId });
  const request = xws(getQuotingDetailsService).get(
    getQuotingDetailsService.getPath()
  );

  try {
    const { data } = await request;
    dispatch(setIsQuoteAppt(true));
    dispatch(setDeepLinkKind('quoteAppt'));
    dispatch(setVerifiedVehicle(true));
    await dispatch(setQuotingAppointment(data));
  } catch (err) {
    dispatch(setIsQuoteAppt(false));
    dispatch(setShowQuoteApptError(true));
    /* eslint-disable no-console */
    console.error('>>error', err);
    /* eslint-enable no-console */
  }
};

const defineNavigation = () => async (dispatch, getState) => {
  const scope = getQueryParam('scope')(getState());
  const isApptRevisit = getApptRevisit(getState());
  const hasServices = getHasServices(getState());
  const hasTransportationOption = getHasTransportationOptions(getState());
  const transporationOption = getTransportation(getState());
  const isSupportedTransportation =
    transporationOption !== VALET && transporationOption !== VALETLOANER;
  const isVerifiedVehicle = getVerifiedVehicle(getState());
  const isIncompleteVehicle = getIncompleteVehicle(getState());
  const pastAppointment = isPastAppointment(getState());
  const deeplinkKind = getDeeplinkKind(getState());
  if (
    (deeplinkKind === 'autoLogin' || deeplinkKind === 'sso') &&
    !isVerifiedVehicle
  ) {
    dispatch(getStep('SH'));
  }
  if (pastAppointment && deeplinkKind !== 'quickAppt' && scope !== NEW_APPT) {
    dispatch(getStep('START'));
    return;
  }
  if (isApptRevisit || deeplinkKind === 'quickAppt') {
    // step REVIEW || dest
    dispatch(getStep('REVIEW'));
    return;
  }
  if (
    (hasTransportationOption && isSupportedTransportation) ||
    deeplinkKind === 'SCI'
  ) {
    // step APPT || dest < REVIEW
    await dispatch(getStep('APPT'));
    return;
  }
  if (hasServices) {
    // step TRANS || dest < APPT
    await dispatch(getStep('TRANS'));
    return;
  }
  if (isVerifiedVehicle) {
    // step SERVICE || dest < APPT
    dispatch(getStep('SERVICE'));
    return;
  }
  if (deeplinkKind === 'quoteAppt') {
    dispatch(getStep('SERVICE'));
    return;
  }
  if (isIncompleteVehicle) {
    // step VEHICLE || dest < APPT
    dispatch(getStep('VEHICLE'));
  }
  // step start screen
};

const pushGTMStartEvent = () => (dispatch, getState) => {
  dispatch(gtmSetDimensionConfirmationKey(getConfirmationKey(getState())));
  dispatch(gtmStartGenericEvent());
  dispatch(gtmSetDimensionConfirmationKey(undefined));
};

const setVerifiedVehicle = verified => {
  return {
    type: actionTypes.SET_VERIFIED_VEHICLE,
    payload: verified
  };
};

const setIsIncompleteVehicle = verified => {
  return {
    type: actionTypes.SET_INCOMPLETE_VEHICLE,
    payload: verified
  };
};

export const setIsDeeplinking = deeplinking => {
  return {
    type: actionTypes.SET_IS_DEEPLINKING,
    payload: deeplinking
  };
};

const setIsDeeplink = isDeeplink => {
  return {
    type: actionTypes.SET_IS_DEEPLINK,
    payload: isDeeplink
  };
};

const setDeepLinkKind = kind => {
  return {
    type: actionTypes.SET_DEEPLINK_KIND,
    payload: kind
  };
};

const setAppRevisit = revisit => {
  return {
    type: actionTypes.SET_APPT_REVISIT,
    payload: revisit
  };
};

const setHasServices = hasServices => {
  return {
    type: actionTypes.SET_HAS_SERVICES,
    payload: hasServices
  };
};

const setHasTransportationOptions = hasTransportationOptions => {
  return {
    type: actionTypes.SET_HAS_TRANSPORTATION_OPTIONS,
    payload: hasTransportationOptions
  };
};



// WEBPACK FOOTER //
// ./src/store/reducers/deeplink/actions.js